/**
 * Custom TabList component based on Vuexy design.
 * Provides consistent styling and pill variant support.
 * Uses Tabs from @mui/material instead of @mui/lab for compatibility.
 */

// MUI Imports
import { Tabs, styled } from '@mui/material';
import type { TabsProps } from '@mui/material';

// Type Imports
import type { ThemeColor } from '@core/types';

/**
 * Custom TabList props with pill variant support
 */
export type CustomTabListProps = TabsProps & {
  color?: ThemeColor;
  pill?: 'true' | 'false';
};

const TabList = styled(Tabs)<CustomTabListProps>(({ color, theme, pill, orientation }) => ({
  ...(pill === 'true' && {
    minHeight: 38,
    ...(orientation === 'vertical'
      ? {
          borderInlineEnd: 0,
        }
      : {
          borderBlockEnd: 0,
        }),
    '&, & .MuiTabs-scroller': {
      ...(orientation === 'vertical' && {
        boxSizing: 'content-box',
      }),
      margin: `${theme.spacing(-1, -1, -1.5, -1)} !important`,
      padding: theme.spacing(1, 1, 1.5, 1),
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
    '& .MuiTabs-flexContainer': {
      gap: theme.spacing(1),
    },
    '& .Mui-selected': {
      backgroundColor: `var(--mui-palette-${color}-main) !important`,
      color: `var(--mui-palette-${color}-contrastText) !important`,
      boxShadow: `var(--mui-customShadows-${color}-sm)`,
    },
    '& .MuiTab-root': {
      minHeight: 38,
      padding: theme.spacing(2, 5),
      borderRadius: 'var(--mui-shape-borderRadius)',
      '&:hover': {
        border: 0,
        backgroundColor: `var(--mui-palette-${color}-lightOpacity)`,
        color: `var(--mui-palette-${color}-main)`,
        ...(orientation === 'vertical'
          ? {
              paddingInlineEnd: theme.spacing(5),
            }
          : {
              paddingBlockEnd: theme.spacing(2),
            }),
      },
    },
  }),
}));

/**
 * Custom TabList component with Vuexy styling.
 * @param props - TabList props with custom options
 */
const CustomTabList = (props: CustomTabListProps) => {
  // Props
  const { color = 'primary', ...rest } = props;

  return <TabList color={color} {...rest} />;
};

export default CustomTabList;
